import Icon from '@atoms/Icon/Icon';
import Search from '@atoms/Search/Search';
import Text from '@atoms/Text/Text';
import { ILink } from '@models/ILink';
import { slideTransition } from '@utility/config/animations';
import { motion } from 'motion/react';
import { ForwardedRef, MouseEventHandler, forwardRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

interface Props {
  readonly autoCompleteEndpoint: string;
  readonly mostSearchedTerms: ILink[];
  readonly mostSearchedTermsLabel?: string;
  readonly onCloseCallback?: MouseEventHandler<HTMLButtonElement>;
  readonly quickLinks: ILink[];
  readonly quickLinksLabel?: string;
  readonly searchUrl: string;
  readonly searchPlaceholderLabel: string;
  readonly showAnimations: boolean;
}

const SearchDropdown = forwardRef(
  (
    {
      autoCompleteEndpoint,
      onCloseCallback,
      mostSearchedTerms,
      mostSearchedTermsLabel,
      showAnimations,
      searchUrl,
      searchPlaceholderLabel,
      quickLinks,
      quickLinksLabel,
    }: Props,
    ref,
  ) => {
    const {
      register,
      handleSubmit,
      formState: { errors, isSubmitSuccessful },
      watch,
      setValue,
    } = useForm({
      defaultValues: {},
    });

    const slideDown = showAnimations
      ? {
          animate: {
            y: 0,
          },
          exit: {
            y: '-100%',
          },
        }
      : {
          animate: {},
          exit: {},
        };

    // Submit handler
    const onSubmit: SubmitHandler<{
      [x: string]: string | (string | null)[] | null;
    }> = async (data, e) => {
      e?.preventDefault();

      window.dataLayer.push({
        event: 'trackingEvent_search_results',
        event_name_variable: 'search_results',
        trackingEvent_search_results: {
          search_type: 'navigation',
          search_term: data.freetext,
        },
      });

      const generateSearchUrl = (searchTerm: string) => {
        const searchTermEncoded = encodeURI(searchTerm);
        return `${searchUrl}?freeText=${searchTermEncoded}`;
      };

      window.location.href = generateSearchUrl(data?.freetext as string);
    };

    return (
      <motion.div
        ref={ref as ForwardedRef<HTMLDivElement>}
        variants={slideDown}
        initial="exit"
        animate="animate"
        exit="exit"
        transition={slideTransition}
        key="searchOverlay"
        className="tw-absolute tw-top-[56px] tw-z-40 tw-h-[calc(100vh-56px)] tw-w-full tw-overflow-y-scroll tw-bg-white tw-p-xs tw-pb-[36px] md:tw-top-[64px] md:tw-h-[600px] md:tw-py-[36px] xl:tw-h-[400px] xl:tw-py-[36px] dark:tw-bg-grey-10"
      >
        <div className="tw-container tw-relative tw-mx-auto tw-flex tw-flex-col">
          <button
            type="button"
            className="tw-self-end"
            onClick={onCloseCallback}
            aria-label="menu"
          >
            <Icon name="cross" className="tw-text-2xl" size="1.5rem" />
          </button>
          <div className="tw-mb-7 tw-mt-5 tw-w-full md:tw-mb-xl md:tw-mt-[32px]">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Search
                autoCompleteEndpoint={`${autoCompleteEndpoint}?page=0&pageUrl=${window.location.pathname}&freetext`}
                autoFocus
                label={'Search'}
                large={true}
                name={'freetext'}
                placeholder={searchPlaceholderLabel}
                requiredErrorMessage={'error'}
                register={register}
                searchClasses="py-[10px]"
                watch={watch}
                setValue={setValue}
              />
            </form>
          </div>
          <div className="tw-flex">
            {!!mostSearchedTerms?.length && (
              <div className="tw-flex tw-w-full tw-flex-col md:tw-w-3/4">
                {mostSearchedTermsLabel && (
                  <Text
                    variant="body"
                    className="tw-text-sm tw-font-light tw-text-grey-60"
                  >
                    {mostSearchedTermsLabel}
                  </Text>
                )}
                <ul className="tw-mt-md tw-grid tw-grid-cols-1 tw-gap-3 md:tw-mt-sm md:tw-grid-cols-3">
                  {mostSearchedTerms.map(({ text, url }) => (
                    <li key={text}>
                      <a href={url}>
                        <Text
                          variant="body"
                          className="tw-text-sm tw-text-grey-100 hover:tw-text-grey-50"
                        >
                          {text}
                        </Text>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!!quickLinks?.length && (
              <div className="tw-flex tw-w-full tw-flex-col md:tw-w-1/4">
                {quickLinksLabel && (
                  <Text
                    variant="body"
                    className="tw-text-sm tw-font-light tw-text-grey-60"
                  >
                    {quickLinksLabel}
                  </Text>
                )}
                <ul className="tw-mt-md tw-flex tw-flex-col tw-items-start tw-gap-3 md:tw-mt-sm">
                  {quickLinks.map(({ text, url, target }) => (
                    <li key={text}>
                      <a
                        key={text}
                        href={url}
                        target={target}
                        className="tw-flex tw-items-center tw-gap-[5px]"
                      >
                        <Text
                          variant="body"
                          className="tw-text-sm tw-text-grey-100 hover:tw-text-grey-50"
                        >
                          {text}
                        </Text>
                        {target === '_blank' && (
                          <Icon
                            name="external-link"
                            className="tw-text-[18px] tw-text-grey-70"
                          />
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  },
);
SearchDropdown.displayName = 'SearchDropdown';

export default SearchDropdown;
